import { I18n } from '@affine/i18n';

import type { StreamObject } from '../components/ai-chat-messages';

export type PersistedToolResult = {
  toolCallId: string;
  toolName: string;
  displayName?: string | null;
  success: boolean;
  result?: unknown;
  durationMs?: number | null;
};

function streamObjectToolCallIds(streamObjects?: StreamObject[] | null) {
  return new Set(
    (streamObjects ?? [])
      .filter(
        streamObject =>
          streamObject.type === 'tool-call' ||
          streamObject.type === 'tool-result'
      )
      .map(streamObject => streamObject.toolCallId)
      .filter((toolCallId): toolCallId is string => !!toolCallId)
  );
}

/**
 * History can contain final text stream objects before its embedded tool result
 * summaries have been folded into that same array. Keep the live cards until
 * persisted history covers every live tool call instead of comparing text only.
 */
function hasUnpersistedToolEvidence({
  currentStreamObjects,
  persistedStreamObjects,
  persistedToolResults,
}: {
  currentStreamObjects?: StreamObject[] | null;
  persistedStreamObjects?: StreamObject[] | null;
  persistedToolResults?: readonly PersistedToolResult[] | null;
}) {
  const currentToolCallIds = streamObjectToolCallIds(currentStreamObjects);
  if (!currentToolCallIds.size) return false;

  const persistedToolCallIds = streamObjectToolCallIds(persistedStreamObjects);
  for (const result of persistedToolResults ?? []) {
    if (result.toolCallId) persistedToolCallIds.add(result.toolCallId);
  }
  return [...currentToolCallIds].some(id => !persistedToolCallIds.has(id));
}

/**
 * Persisted tool summaries are authoritative history data. Reconcile them into
 * stream objects even when the server already returned text-only streamObjects;
 * otherwise the renderer skips its history reconstruction path and cards vanish.
 */
export function reconcilePersistedToolResults(
  streamObjects: readonly StreamObject[] | null | undefined,
  toolResults: readonly PersistedToolResult[] | null | undefined
): StreamObject[] {
  const objects = [...(streamObjects ?? [])];
  if (!toolResults?.length) return objects;

  const resultById = new Map(
    toolResults.map(result => [result.toolCallId, result])
  );
  const coveredIds = new Set<string>();
  const reconciled = objects.map(object => {
    if (object.type !== 'tool-call' && object.type !== 'tool-result') {
      return object;
    }
    const persisted = resultById.get(object.toolCallId);
    if (!persisted) return object;
    coveredIds.add(persisted.toolCallId);
    return {
      ...object,
      type: 'tool-result' as const,
      toolCallId: persisted.toolCallId,
      toolName: persisted.toolName,
      ...(persisted.displayName ? { displayName: persisted.displayName } : {}),
      ...(typeof persisted.durationMs === 'number'
        ? { durationMs: persisted.durationMs }
        : {}),
      result:
        persisted.result ??
        (object.type === 'tool-result'
          ? object.result
          : { success: persisted.success }),
    };
  });
  const missingResults = toolResults
    .filter(result => !coveredIds.has(result.toolCallId))
    .map(result => ({
      type: 'tool-result' as const,
      toolCallId: result.toolCallId,
      toolName: result.toolName,
      ...(result.displayName ? { displayName: result.displayName } : {}),
      ...(typeof result.durationMs === 'number'
        ? { durationMs: result.durationMs }
        : {}),
      args: {},
      result: result.result ?? { success: result.success },
    }));
  if (!missingResults.length) return reconciled;

  const answerIndex = reconciled.findIndex(
    object => object.type === 'text-delta'
  );
  const insertionIndex = answerIndex === -1 ? reconciled.length : answerIndex;
  reconciled.splice(insertionIndex, 0, ...missingResults);
  return reconciled;
}

export function getStreamObjectTextLength(
  streamObjects?: StreamObject[] | null
) {
  if (!streamObjects?.length) {
    return 0;
  }

  return streamObjects.reduce((length, streamObject) => {
    if (streamObject.type === 'text-delta') {
      return length + streamObject.textDelta.trim().length;
    }
    return length;
  }, 0);
}

/**
 * The stream objects of a message together with the text it is stored with. Live objects
 * that carry no answer text (tool rows, reasoning) get the stored text as their answer, so
 * a sentence the server wrote into the message is shown.
 */
export function withStoredText(
  streamObjects: StreamObject[],
  storedText: string
): StreamObject[] {
  if (!storedText.trim() || getStreamObjectTextLength(streamObjects) > 0) {
    return streamObjects;
  }
  return [...streamObjects, { type: 'text-delta', textDelta: storedText }];
}

export function getStreamObjectReasoningLength(
  streamObjects?: StreamObject[] | null
) {
  if (!streamObjects?.length) {
    return 0;
  }

  return streamObjects.reduce((length, streamObject) => {
    if (streamObject.type === 'reasoning') {
      return length + streamObject.textDelta.trim().length;
    }
    return length;
  }, 0);
}

export function shouldPreserveCurrentStreamObjects({
  currentStreamObjects,
  persistedContent,
  persistedStreamObjects,
  persistedToolResults,
}: {
  currentStreamObjects?: StreamObject[] | null;
  persistedContent?: string | null;
  persistedStreamObjects?: StreamObject[] | null;
  persistedToolResults?: readonly PersistedToolResult[] | null;
}) {
  if (
    hasUnpersistedToolEvidence({
      currentStreamObjects,
      persistedStreamObjects,
      persistedToolResults,
    })
  ) {
    return true;
  }

  const currentTextLength = getStreamObjectTextLength(currentStreamObjects);
  const persistedTextLength = Math.max(
    getStreamObjectTextLength(persistedStreamObjects),
    persistedContent?.trim().length ?? 0
  );

  if (currentTextLength > persistedTextLength) {
    return true;
  }

  if (currentTextLength > 0 || persistedTextLength > 0) {
    return false;
  }

  return (
    getStreamObjectReasoningLength(currentStreamObjects) >
    getStreamObjectReasoningLength(persistedStreamObjects)
  );
}

/** What a chat message holds for the hand-over of live stream objects to its stored copy. */
export interface StreamObjectCarrier {
  id?: string | null;
  role: string;
  content?: string | null;
  messageType?: string | null;
  streamObjects?: StreamObject[] | null;
  toolData?: {
    toolResults?: readonly PersistedToolResult[] | null;
  } | null;
}

// An ask and the result of a tool are filed as messages of their own next to the answer.
const NON_ANSWER_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'tool_approval_request',
  'tool_approval_response',
  'tool_result',
]);

/**
 * Hands the stream objects of the messages on screen over to the history that replaces them.
 * A stored message takes those of the message with its id. The answer the client wrote itself
 * while it streamed has no stored id yet: the newest stored answer the screen does not know by
 * id takes it. A message history knows by id never lends its objects to another message, so a
 * sentence the server files after the answer shows its own text.
 */
export function carryLiveStreamObjects<
  Item,
  Message extends Item & StreamObjectCarrier,
>(
  current: readonly Message[],
  stored: readonly Item[],
  isMessage: (item: Item) => item is Message
): Item[] {
  const storedIds = new Set(
    stored.filter(isMessage).map(message => message.id)
  );
  const currentIds = new Set(current.map(message => message.id));
  const currentById = new Map(
    current
      .filter(message => !!message.id && !!message.streamObjects?.length)
      .map(message => [message.id, message])
  );
  const unstored = current.filter(
    message => !message.id || !storedIds.has(message.id)
  );
  const next = stored.slice();
  let unstoredIndex = unstored.length - 1;

  for (let index = next.length - 1; index >= 0; index--) {
    const message = next[index];
    if (!isMessage(message)) {
      continue;
    }

    let live = message.id ? currentById.get(message.id) : undefined;
    const knownOnScreen = !!message.id && currentIds.has(message.id);
    if (
      !live &&
      !knownOnScreen &&
      !NON_ANSWER_MESSAGE_TYPES.has(message.messageType ?? '')
    ) {
      while (unstoredIndex >= 0) {
        const candidate = unstored[unstoredIndex];
        unstoredIndex--;
        if (candidate.role === message.role) {
          live = candidate;
          break;
        }
      }
    }
    if (!live?.streamObjects?.length) {
      continue;
    }

    if (
      !shouldPreserveCurrentStreamObjects({
        currentStreamObjects: live.streamObjects,
        persistedContent: message.content,
        persistedStreamObjects: message.streamObjects,
        persistedToolResults: message.toolData?.toolResults,
      })
    ) {
      continue;
    }

    next[index] = { ...message, streamObjects: live.streamObjects };
  }

  return next;
}

// Reasoning summaries arrive as consecutive "**Title**\n\nBody" parts with no
// separator, so a new title sticks to the previous sentence ("costs.**Next**").
const GLUED_REASONING_TITLE = /([^\s*])\*\*(?=[^\s*][^*\n]*\*\*\n)/g;

/** Starts every reasoning summary part on its own paragraph. */
export function formatReasoningText(text: string): string {
  return text.replace(GLUED_REASONING_TITLE, '$1\n\n**');
}

export function mergeStreamObjects(chunks: StreamObject[] = []) {
  return chunks.reduce((acc, curr) => {
    const prev = acc.at(-1);
    switch (curr.type) {
      case 'reasoning': {
        const reasoningIndex = acc.findIndex(item => item.type === 'reasoning');
        if (reasoningIndex !== -1) {
          const previousReasoning = acc[reasoningIndex];
          if (previousReasoning.type === 'reasoning') {
            acc[reasoningIndex] = {
              ...previousReasoning,
              textDelta: previousReasoning.textDelta + curr.textDelta,
            };
          }
        } else {
          acc.push(curr);
        }
        break;
      }
      case 'text-delta': {
        if (prev && prev.type === curr.type) {
          acc[acc.length - 1] = {
            ...prev,
            textDelta: prev.textDelta + curr.textDelta,
          };
        } else {
          acc.push(curr);
        }
        break;
      }
      case 'tool-call': {
        // sl-718z: Replace existing tool-call with same toolCallId (e.g. start → full)
        const tcIdx = curr.toolCallId
          ? acc.findIndex(
              item =>
                item.type === 'tool-call' && item.toolCallId === curr.toolCallId
            )
          : -1;
        if (tcIdx !== -1) {
          acc[tcIdx] = curr;
        } else {
          acc.push(curr);
        }
        break;
      }
      case 'tool-result': {
        // sl-z4tb.5: Remove tool-execution-started banner when results arrive
        const tesIdx = acc.findIndex(
          item => item.type === 'tool-execution-started'
        );
        if (tesIdx !== -1) {
          acc.splice(tesIdx, 1);
        }
        const index = acc.findIndex(
          item =>
            (item.type === 'tool-call' || item.type === 'tool-result') &&
            item.toolCallId === curr.toolCallId
        );
        if (index !== -1) {
          acc[index] = curr;
        } else {
          acc.push(curr);
        }
        break;
      }
      case 'tool-execution-started': {
        // sl-z4tb.5: Replace previous execution-started banner
        const prevTes = acc.findIndex(
          item => item.type === 'tool-execution-started'
        );
        if (prevTes !== -1) {
          acc[prevTes] = curr;
        } else {
          acc.push(curr);
        }
        break;
      }
      default: {
        acc.push(curr);
        break;
      }
    }
    return acc;
  }, [] as StreamObject[]);
}

export function mergeStreamContent(chunks: StreamObject[]): string {
  return chunks.reduce((acc, curr) => {
    if (curr.type === 'text-delta') {
      acc += curr.textDelta;
    }
    return acc;
  }, '');
}

/**
 * sl-z4tb.5: Group consecutive tool-results with the same toolName
 * into summary objects for compact display.
 */
export interface ToolResultGroup {
  type: 'tool-result-group';
  toolName: string;
  displayName?: string;
  count: number;
  results: StreamObject[];
}

export function groupConsecutiveResults(
  items: StreamObject[]
): Array<StreamObject | ToolResultGroup> {
  const output: Array<StreamObject | ToolResultGroup> = [];
  for (const item of items) {
    if (item.type === 'tool-result') {
      const last = output.at(-1);
      if (
        last &&
        'type' in last &&
        last.type === 'tool-result-group' &&
        last.toolName === item.toolName
      ) {
        last.count++;
        last.results.push(item);
      } else if (
        last &&
        last.type === 'tool-result' &&
        last.toolName === item.toolName
      ) {
        output[output.length - 1] = {
          type: 'tool-result-group',
          toolName: item.toolName,
          displayName:
            item.displayName || (last as { displayName?: string }).displayName,
          count: 2,
          results: [last, item],
        };
      } else {
        output.push(item);
      }
    } else {
      output.push(item);
    }
  }
  return output;
}

/**
 * sl-55fc.4: Format raw tool name to human-readable form.
 * Only used when no displayName is available (e.g. tool-execution-started banner).
 * Backend resolveToolDisplayName() is the primary source of truth.
 */
export function humanToolName(toolName: string): string {
  return toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function i18nOrFallback(
  key: string,
  fallback: string,
  options?: Record<string, unknown>
): string {
  const translated = I18n.t(key, options);
  return translated === key ? fallback : translated;
}

export function localizedToolName(
  toolName: string,
  fallbackDisplayName?: string
): string {
  return i18nOrFallback(
    `com.streamlain.ai.tool.${toolName}`,
    fallbackDisplayName || humanToolName(toolName)
  );
}

export function localizedToolCallName(
  toolName: string,
  fallbackDisplayName?: string
): string {
  const tool = localizedToolName(toolName, fallbackDisplayName);
  const stateKey = `com.streamlain.ai.tool.${toolName}.status.running`;
  const stateLabel = I18n.t(stateKey);
  if (stateLabel !== stateKey) return stateLabel;
  return i18nOrFallback('com.streamlain.ai.tool.status.running', tool, {
    tool,
  });
}

/** What became of a tool call: it ran, it failed, or the user said no before it ran. */
export type ToolResultStatus = 'completed' | 'failed' | 'cancelled';

/** A call the user said no to did not fail: its stored result keeps this code. */
export function toolResultStatus(result: unknown): ToolResultStatus {
  if (!result || typeof result !== 'object') return 'completed';
  const { success, errorCode } = result as {
    success?: unknown;
    errorCode?: unknown;
  };
  if (success !== false) return 'completed';
  return errorCode === 'tool_confirmation_rejected' ? 'cancelled' : 'failed';
}

export function localizedToolResultName(
  toolName: string,
  status: ToolResultStatus,
  fallbackDisplayName?: string
): string {
  const tool = localizedToolName(toolName, fallbackDisplayName);
  const stateKey = `com.streamlain.ai.tool.${toolName}.status.${status}`;
  const stateLabel = I18n.t(stateKey);
  if (stateLabel !== stateKey) return stateLabel;
  return i18nOrFallback(`com.streamlain.ai.tool.status.${status}`, tool, {
    tool,
  });
}

export function localizedToolGroupResultName(
  toolName: string,
  status: ToolResultStatus,
  count: number,
  fallbackDisplayName?: string
): string {
  const tool = localizedToolName(toolName, fallbackDisplayName);
  const stateKey = `com.streamlain.ai.tool.${toolName}.status.group-${status}`;
  const stateLabel = I18n.t(stateKey, { count });
  if (stateLabel !== stateKey) return stateLabel;
  return i18nOrFallback(
    `com.streamlain.ai.tool.status.group-${status}`,
    `${tool} (${count}x) ${status}`,
    { tool, count }
  );
}

export function localizedToolExecutionStartedName(summary: string): string {
  return i18nOrFallback(
    'com.streamlain.ai.tool.status.executing',
    `Executing: ${summary}`,
    { tools: summary }
  );
}

export function localizedToolText(
  key: string,
  fallback: string,
  options?: Record<string, unknown>
): string {
  return i18nOrFallback(key, fallback, options);
}
